import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { StyledOcticon, Timeline, Tooltip } from '@primer/react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import Button from '../../common/Button/Button';
import ErrorLabel from '../../common/Labels/ErrorLabel';
import IndexIcon from '../../common/IndexIcon/IndexIcon';
import StepIcon from '../../common/icons/StepIcon';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';

import PipelineStepContent from './PipelineStepContent';

const IndexedIcon = props => <IndexIcon index={props.index + 1} {...props} className="w-full h-full" />

const PipelineStepItem = ({
  index, step, numberOfSteps, onDelete, deployed, readOnly
}) => {
  const isDisabled = deployed || readOnly;
  const tooltipLabel = readOnly
    ? 'Insufficient workspace permissions'
    : 'You must undeploy this Pipeline before editing it.';

  return (
    <Timeline.Item className="items-center">
      <Timeline.Badge className="before:bg-border-dark">
        {/* Oticon requires an index, but cannot pass that in without defining the component inside of render */}
        {/* eslint-disable-next-line */}
        <StyledOcticon icon={() => <IndexedIcon index={index} />} />
      </Timeline.Badge>
      <Timeline.Body>
        <div className={classNames('p-1 flex items-center justify-between', { 'border-b-1': numberOfSteps - 1 !== index })}>
          <PipelineStepContent step={step} />
          {
            (index > 0) ? (
              isDisabled ? (
                <Tooltip aria-label={tooltipLabel} direction="w" noDelay wrap>
                  <Button variant="remove" onClick={onDelete} disabled={isDisabled} />
                </Tooltip>
              ) : <Button variant="remove" onClick={onDelete} />
            ) : null
          }
        </div>
      </Timeline.Body>
    </Timeline.Item>
  )
}

const PipelineStepRow = ({ data, index, style }) => {
  const rowRef = useRef({});
  const {
    listRef,
    rowHeights,
    numberOfSteps,
    onDeleteStep,
    deployed,
    readOnly,
    items
  } = data
  const setRowHeight = (rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  };

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
    // eslint-disable-next-line
  }, [index]);

  return (
    <div style={style}>
      <PipelineStepItem
        key={items[index].id}
        step={items[index]}
        index={index}
        numberOfSteps={numberOfSteps}
        onDelete={() => onDeleteStep(index)}
        deployed={deployed}
        readOnly={readOnly}
      />
    </div>
  )
};

export const PipelineStepListContent = ({
  items,
  error,
  onDeleteStep,
  deployed,
  readOnly = false,
  height = 240,
  disableScrollFading = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const numberOfSteps = items?.length || 0;

  const noDataMessage = error
    ? 'There was an error retrieving step data for this pipeline...'
    : 'No Steps to show for this pipeline...'

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 76;
  }, [rowHeights]);

  if (!items?.length) {
    return null;
  }

  return !numberOfSteps ? (
    <div className="p-4 flex-grow flex flex-col justify-center items-center">
      <StyledOcticon
        className="mb-2 text-brand"
        icon={StepIcon}
        size={16}
      />
      <ErrorLabel>{noDataMessage}</ErrorLabel>
    </div>
  ) : (
    <div className="overflow-scroll px-6 flex-grow">
      {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
      <Timeline sx={{ zIndex: 1 }}>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
          )
        }
        <AutoSizer disableHeight>
          {({ width }) => (
            items?.length && (
              <VariableSizeList
                ref={listRef}
                height={height}
                itemCount={numberOfSteps}
                estimatedItemSize={76}
                itemSize={getRowHeight}
                width={width}
                itemData={{
                  listRef,
                  rowHeights,
                  numberOfSteps,
                  onDeleteStep,
                  deployed,
                  readOnly,
                  items
                }}
              >
                {PipelineStepRow}
              </VariableSizeList>
            )
          )}
        </AutoSizer>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          )
        }
      </Timeline>
    </div>
  )
}

export default PipelineStepListContent;
